/* Header menu behaviour, matching the captured source.

   The source CSS is already in this theme and carries the whole contract:
     .menu-toggleable-left.navbar-toggleable-md { left:-100%; transition:left .5s cubic-bezier(0,1,.5,1) }
     .menu-toggleable-left.navbar-toggleable-md.in { left:0 }
     .menu-toggleable-left.in .dropdown-menu { left:-100vw; transition:left .5s ... }
     .menu-toggleable-left.in .show > .dropdown-menu { left:0 }
     .navbar.bg-inverse .nav-item > .nav-link + .dropdown-menu.show { visibility:visible }
   Only the script that toggles those classes was lost when the capture was sanitised. This file
   restores it: on desktop the panel opens on hover and the link keeps navigating; on mobile the
   drawer slides in, drills one level down with the back/title bar, and slides back out.

   The shared theme script binds a different, non-source behaviour to the same hooks (click to open
   on desktop, `us-menu-open` with no transition on mobile). Rather than depend on which script runs
   first, the clicks are intercepted in the capture phase and stopped there, so only this contract
   reaches the DOM. */
(() => {
  const BREAKPOINT = 1200; // the source drawer rules live under max-width:1199.98px
  const desktop = () => window.innerWidth >= BREAKPOINT;
  const HOOKS = '.navbar-toggler, .nav-link.dropdown-toggle, .close-menu button';

  const state = { header: null, menu: null, toggler: null, title: null, items: [] };

  const panelOf = item => item.querySelector(':scope > .dropdown-menu');
  const toggleOf = item => item.querySelector(':scope > .nav-link.dropdown-toggle');

  const closeItem = item => {
    item.classList.remove('show');
    panelOf(item)?.classList.remove('show');
    toggleOf(item)?.setAttribute('aria-expanded', 'false');
  };
  const closeAll = () => state.items.forEach(closeItem);
  const openItem = item => {
    state.items.forEach(other => { if (other !== item) closeItem(other); });
    item.classList.add('show');
    panelOf(item)?.classList.add('show');
    toggleOf(item)?.setAttribute('aria-expanded', 'true');
  };

  let hideTimer = null;
  const openDrawer = () => {
    const { menu, toggler } = state;
    clearTimeout(hideTimer);
    menu.classList.remove('d-none', 'us-menu-open');
    // force layout before adding the class so the slide starts from -100%; requestAnimationFrame
    // would be throttled when the tab is not painting, and the drawer would never open
    void menu.offsetWidth;
    menu.classList.add('in');
    toggler?.setAttribute('aria-expanded', 'true');
  };
  const closeDrawer = () => {
    const { menu, toggler, title } = state;
    menu.classList.remove('in', 'level2-visible', 'us-menu-open');
    closeAll();
    if (title) title.textContent = '';
    toggler?.setAttribute('aria-expanded', 'false');
    hideTimer = setTimeout(() => { if (!menu.classList.contains('in')) menu.classList.add('d-none'); }, 520);
  };
  const goBack = () => {
    const open = state.items.find(item => item.classList.contains('show'));
    if (!open) return closeDrawer();
    closeItem(open);
    state.menu.classList.remove('level2-visible');
    if (state.title) state.title.textContent = '';
    toggleOf(open)?.focus({ preventScroll: true });
  };
  const drillInto = item => {
    openItem(item);
    state.menu.classList.add('level2-visible');
    if (state.title) state.title.textContent = toggleOf(item)?.textContent.trim() || '';
  };

  const collect = () => {
    const header = document.querySelector('header');
    if (!header) return false;
    state.header = header;
    state.menu = document.querySelector('.main-menu');
    state.toggler = document.querySelector('.navbar-toggler');
    state.title = document.querySelector('.close-menu .level1-title');
    state.items = [...document.querySelectorAll('li.nav-item.dropdown')];
    return !!(state.menu && state.items.length);
  };

  const onCaptureClick = event => {
    const hook = event.target.closest?.(HOOKS);
    if (!hook || !state.menu) return;
    if (hook.matches('.navbar-toggler')) {
      event.preventDefault();
      event.stopPropagation();
      state.menu.classList.contains('in') ? closeDrawer() : openDrawer();
      return;
    }
    if (hook.matches('.close-menu button')) {
      event.preventDefault();
      event.stopPropagation();
      hook.closest('.back') ? goBack() : closeDrawer();
      return;
    }
    // level-1 link: on desktop the source lets it navigate, on mobile it drills down
    if (desktop()) { event.stopPropagation(); return; }
    const item = hook.closest('li.nav-item.dropdown');
    if (!item) return;
    event.preventDefault();
    event.stopPropagation();
    drillInto(item);
  };

  const onOver = event => {
    if (!desktop()) return;
    const item = event.target.closest?.('li.nav-item.dropdown');
    if (item && state.items.includes(item)) openItem(item);
    else if (!event.target.closest?.('.dropdown-menu')) closeAll();
  };

  const start = () => {
    if (!collect()) return;
    document.addEventListener('click', onCaptureClick, true);
    document.addEventListener('mouseover', onOver);
    document.addEventListener('focusin', event => {
      if (!desktop()) return;
      const item = event.target.closest?.('li.nav-item.dropdown');
      if (item) openItem(item);
    });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      if (state.menu.classList.contains('in')) state.menu.classList.contains('level2-visible') ? goBack() : closeDrawer();
      else closeAll();
    });
    window.addEventListener('resize', () => {
      if (desktop() && state.menu.classList.contains('in')) closeDrawer();
      if (!desktop()) closeAll();
    });
    document.documentElement.dataset.snMenu = 'source-contract';
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', () => { collect(); });
})();
